<template>
  <b-modal
    :visible="visible"
    :title="titulo"
    size="lg"
    scrollable
    ok-only
    ok-title="Fechar"
    @hide="$emit('update:visible', false)"
    @show="carregar"
  >
    <div
      v-if="loading"
      class="text-center py-4"
    >
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="erro"
      class="text-center py-4"
    >
      <p class="text-muted mb-1">
        {{ erro }}
      </p>
      <button
        class="btn btn-sm btn-outline-secondary"
        @click="carregar"
      >
        Tentar de novo
      </button>
    </div>

    <div v-else-if="occurrence">
      <!-- Cabeçalho -->
      <div class="od-head">
        <span
          class="badge text-monospace od-protocol"
          :style="protocoloEstilo"
        >{{ occurrence.protocol || `#${occurrence.id}` }}</span>

        <span :class="situacao.classe">{{ situacao.rotulo }}</span>

        <span
          v-if="occurrence.classification"
          class="badge"
          :style="classificationBadgeEstilo"
        >{{ occurrence.classification.color_label }}</span>

        <span class="text-muted ml-auto od-step">
          {{ etapaRotulo }}
        </span>
      </div>

      <!-- Dados -->
      <dl class="od-grid">
        <div>
          <dt>Solicitante</dt>
          <dd>{{ occurrence.requester_name || '—' }}</dd>
        </div>
        <div>
          <dt>Telefone</dt>
          <dd>{{ occurrence.requester_phone || '—' }}</dd>
        </div>
        <div class="od-grid__wide">
          <dt>Endereço</dt>
          <dd>{{ endereco }}</dd>
        </div>
        <div>
          <dt>Tipo</dt>
          <dd>{{ occurrence.dispatch_type ? occurrence.dispatch_type.name : '—' }}</dd>
        </div>
        <div>
          <dt>Motivo</dt>
          <dd>{{ occurrence.dispatch_reason ? occurrence.dispatch_reason.name : '—' }}</dd>
        </div>
        <div>
          <dt>Disciplina</dt>
          <dd>{{ occurrence.discipline ? occurrence.discipline.name : '—' }}</dd>
        </div>
        <div>
          <dt>Aberta em</dt>
          <dd>{{ formatar(occurrence.created_at) }}</dd>
        </div>
        <div v-if="occurrence.finalized_at">
          <dt>Finalizada em</dt>
          <dd>{{ formatar(occurrence.finalized_at) }}</dd>
        </div>
        <div v-if="occurrence.outcome">
          <dt>Desfecho</dt>
          <dd>{{ occurrence.outcome.name }}</dd>
        </div>
        <div
          v-if="occurrence.complaint"
          class="od-grid__wide"
        >
          <dt>Queixa</dt>
          <dd>{{ occurrence.complaint }}</dd>
        </div>
        <div
          v-if="occurrence.outcome_observation"
          class="od-grid__wide"
        >
          <dt>Observação do desfecho</dt>
          <dd>{{ occurrence.outcome_observation }}</dd>
        </div>
      </dl>

      <!-- Envolvidos -->
      <div class="od-section">
        <div class="od-section__title">
          Envolvidos
          <b-spinner
            v-if="loadingInvolved"
            small
          />
          <span
            v-else
            class="badge badge-light ml-50"
          >{{ involveds.length }}</span>
        </div>

        <p
          v-if="!loadingInvolved && !involveds.length"
          class="text-muted mb-0"
        >
          Nenhum envolvido registrado.
        </p>

        <ul
          v-else
          class="od-list"
        >
          <li
            v-for="env in involveds"
            :key="env.id"
          >
            <strong>{{ nomeEnvolvido(env) }}</strong>
            <span
              v-if="env.role"
              class="text-muted"
            > — {{ env.role.label }}</span>
          </li>
        </ul>
      </div>

      <!-- Despachos -->
      <div class="od-section">
        <div class="od-section__title">
          Despachos
          <span class="badge badge-light ml-50">{{ dispatches.length }}</span>
        </div>

        <p
          v-if="!dispatches.length"
          class="text-muted mb-0"
        >
          Nenhuma equipe despachada.
        </p>

        <ul
          v-else
          class="od-list"
        >
          <li
            v-for="d in dispatches"
            :key="d.id"
          >
            <strong>{{ d.team ? d.team.name : `Equipe #${d.team_id}` }}</strong>
            <span class="text-muted">
              — {{ statusDespacho(d.status) }} · despachada {{ formatar(d.dispatched_at) }}
            </span>
          </li>
        </ul>
      </div>

      <!-- Linha do tempo -->
      <div class="od-section">
        <div class="od-section__title">
          Linha do tempo
        </div>
        <occurrence-timeline :occurrence-id="occurrence.id" />
      </div>

      <!-- Mensagens -->
      <div class="od-section">
        <div class="od-section__title">
          Mensagens
        </div>
        <occurrence-messages
          :occurrence-id="occurrence.id"
          origin="tarm"
        />
      </div>
    </div>

    <template #modal-footer="{ ok }">
      <button
        v-if="podeAbrirOperacional"
        class="btn btn-primary mr-auto"
        @click="$emit('open-operational', occurrence)"
      >
        {{ rotuloBotaoOperacional }}
      </button>
      <button
        class="btn btn-outline-secondary"
        @click="ok()"
      >
        Fechar
      </button>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'
import OccurrenceTimeline from '@/components/OccurrenceTimeline.vue'
import OccurrenceMessages from '@/components/OccurrenceMessages.vue'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import { getContrastTextColor } from '@/utils/color'

const ETAPAS = {
  solicita: 'Solicitação',
  regulacao: 'Atendimento',
  despacha: 'Despacho',
}

export default {
  name: 'OccurrenceDetailModal',

  components: {
    BModal, BSpinner, OccurrenceTimeline, OccurrenceMessages,
  },

  mixins: [ApiErrorMixin],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    occurrenceId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      occurrence: null,
      involveds: [],
      loading: false,
      loadingInvolved: false,
      erro: null,
    }
  },

  computed: {
    titulo() {
      return this.occurrence && this.occurrence.protocol
        ? `Ocorrência ${this.occurrence.protocol}`
        : 'Ocorrência'
    },

    situacao() {
      const status = this.occurrence ? Number(this.occurrence.status) : null

      if (status === 2) return { rotulo: 'Finalizada', classe: 'badge badge-secondary' }
      if (status === 0) return { rotulo: 'Rascunho', classe: 'badge badge-warning' }

      return { rotulo: 'Aberta', classe: 'badge badge-success' }
    },

    etapaRotulo() {
      const step = this.occurrence ? this.occurrence.current_step : null
      return ETAPAS[step] || step || '—'
    },

    protocoloEstilo() {
      const cor = (this.occurrence
        && this.occurrence.classification
        && this.occurrence.classification.hex_color) || '#7367f0'

      return { backgroundColor: cor, color: getContrastTextColor(cor) }
    },

    classificationBadgeEstilo() {
      const cor = (this.occurrence.classification && this.occurrence.classification.hex_color) || '#6b7280'
      return { backgroundColor: cor, color: getContrastTextColor(cor) }
    },

    endereco() {
      const o = this.occurrence
      if (!o || !o.address) return '—'

      const numero = o.number ? `, ${o.number}` : ''
      const bairro = o.neighborhood ? ` — ${o.neighborhood}` : ''
      const cidade = o.city ? ` · ${o.city}` : ''

      return `${o.address}${numero}${bairro}${cidade}`
    },

    dispatches() {
      return (this.occurrence && this.occurrence.dispatches) || []
    },

    podeAbrirOperacional() {
      if (!this.occurrence || Number(this.occurrence.status) !== 1) return false

      const step = this.occurrence.current_step

      if (step === 'regulacao') return this.$hasAbility('regulacao.visualizar')
      if (step === 'despacha') return this.$hasAbility('despacho.visualizar')

      return false
    },

    rotuloBotaoOperacional() {
      return this.occurrence && this.occurrence.current_step === 'regulacao'
        ? 'Abrir no Atendimento'
        : 'Abrir no Despacho'
    },
  },

  methods: {
    async carregar() {
      if (!this.occurrenceId) return

      this.loading = true
      this.erro = null
      this.occurrence = null
      this.involveds = []

      try {
        const res = await this.$http.get(
          `${this.$store.state.api}occurrences/${this.occurrenceId}`,
        )
        this.occurrence = res.data.data || res.data
        this.carregarEnvolvidos()
      } catch (e) {
        this.erro = this.apiErrorText(e, 'Não foi possível carregar a ocorrência.')
      } finally {
        this.loading = false
      }
    },

    async carregarEnvolvidos() {
      this.loadingInvolved = true

      try {
        const res = await this.$http.get(
          `${this.$store.state.api}occurrence_involveds`,
          { params: { occurrence_id: this.occurrenceId } },
        )
        this.involveds = res.data.data || res.data || []
      } catch (e) {
        this.involveds = []
      } finally {
        this.loadingInvolved = false
      }
    },

    nomeEnvolvido(env) {
      const entidade = env.entity || env.involved

      if (!entidade) return `Envolvido #${env.id}`

      return entidade.social_name
        || entidade.name
        || entidade.plate
        || entidade.description
        || `Envolvido #${env.id}`
    },

    statusDespacho(status) {
      const rotulos = { 1: 'em andamento', 2: 'cancelado', 3: 'concluído' }
      return rotulos[Number(status)] || '—'
    },

    formatar(valor) {
      if (!valor) return '—'
      return new Date(valor).toLocaleString('pt-BR')
    },
  },
}
</script>

<style scoped>
.od-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #ebe9f1;
}
.dark-layout .od-head {
  border-bottom-color: #3b4253;
}
.od-protocol {
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}
.od-step {
  font-size: 0.82rem;
}

.od-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px 18px;
  margin-bottom: 4px;
}
.od-grid__wide {
  grid-column: 1 / -1;
}
.od-grid dt {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #b9b9c3;
  font-weight: 600;
  margin-bottom: 2px;
}
.od-grid dd {
  margin: 0;
  font-size: 0.9rem;
  word-break: break-word;
}

.od-section {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #ebe9f1;
}
.dark-layout .od-section {
  border-top-color: #3b4253;
}
.od-section__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.88rem;
  margin-bottom: 8px;
}

.od-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.88rem;
  line-height: 1.7;
}
</style>
